import { deviceAdminApi } from '@/api';
import { InformationMessages, Modal, NotificationStatus, Select } from '@/components/common';
import { handleResponse } from '@/Helper';
import { ResponseObject, SelectOptions } from '@/model/CommonModel';
import { DownloadTSFirmware } from '@/model/DeviceAdminModel';
import { Button, Card, Checkbox, Descriptions } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { useContext, useEffect, useState } from 'react';
import { DoorStoreSingleContext } from '../../../DoorModal/DoorStoreSingleContext';
import { setExitReaderApplyToAllReaders, setExitReaderTSFirmware } from '../exitReaderContext';
import styles from './updateFirmwareModal.module.scss';

type Props = {
	firmwareTitle: string;
	visible: boolean;
	isVeridtReader: boolean;
	closeModal: () => void;
	handleDownloadFirmware: () => void;
};

const UpdateFirmwareModal: React.FC<Props> = ({ visible, firmwareTitle, isVeridtReader, handleDownloadFirmware, closeModal }) => {
	//#region State Management Variables
	const {
		contextStateDoor: {
			exitReader: {
				selections: { tSFirmware, rS485ReaderType },
				applyToAllReaders,
				name,
				id,
				index,
				controllerId,
			},
		},
		dispatcherDoor,
	} = useContext(DoorStoreSingleContext);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [firmwares, setFirmwares] = useState<SelectOptions<string>[]>([]);
	const [warningMessage, setWarningMessage] = useState<JSX.Element>(<></>);
	//#endregion

	useEffect(() => {
		deviceAdminApi.getTSFirmwares(rS485ReaderType).then(item => {
			const firmwareOptions: SelectOptions<string>[] = [];
			if (!handleResponse(item)) {
				const fData = item.Entity;
				if (fData) {
					fData.forEach(fd => {
						firmwareOptions.push({
							label: fd.Name,
							value: fd.Id.toString(),
							id: fd.Id.toString(),
						});
					});
				}
			}
			setFirmwares(firmwareOptions);
			if (firmwareOptions.length > 0) {
				handleOnSetTSFirmware(firmwareOptions[0].value);
			}
		});
		deviceAdminApi.getFirmwareWarningString(rS485ReaderType).then(item => {
			setWarningMessage(<InformationMessages messages={item.Entity.split('\n')} />);
		});
	}, [rS485ReaderType]);

	const handleUpdateFirmware = () => {
		setDownloadLoading(true);
		const tsFirmwareInfo: DownloadTSFirmware = {
			ControllerId: controllerId,
			ReaderIndex: index,
			ReaderId: id,
			ReaderName: name,
			FirmwareId: tSFirmware,
			ApplyToAllReaders: applyToAllReaders,
			RS485ReaderType: rS485ReaderType,
		};
		deviceAdminApi.downloadFirmware(tsFirmwareInfo).then((res: ResponseObject) => {
			NotificationStatus({
				responseData: res,
				onSuccessCallback: () => {
					handleDownloadFirmware();
					closeModal();
				},
			});
			setDownloadLoading(false);
		});
	};

	const handleOnSetTSFirmware = (value: string) => {
		dispatcherDoor(setExitReaderTSFirmware(Number(value)));
	};

	const handleOnApplyToAllReaders = (value: CheckboxChangeEvent) => {
		dispatcherDoor(setExitReaderApplyToAllReaders(value.target.checked));
	};

	return (
		<Modal
			destroyOnClose
			className={styles.modalAutoZoom}
			visible={visible}
			title={firmwareTitle}
			onClickOk={handleUpdateFirmware}
			onCancel={closeModal}
			width={'900px'}
			footer={[
				<Button key='download' type='primary' loading={downloadLoading} onClick={handleUpdateFirmware} disabled={tSFirmware === 0}>
					{_('Download')}
				</Button>,
				<Button
					key='cancel'
					onClick={() => {
						if (!firmwares.length) {
							setFirmwares([
								{
									label: 'No firmware binaries available',
									value: '0',
									id: '0',
								},
							]);
							dispatcherDoor(setExitReaderTSFirmware(0));
						} else {
							setFirmwares(firmwares);
							dispatcherDoor(setExitReaderTSFirmware(tSFirmware));
						}
						setDownloadLoading(false);
						closeModal();
					}}>
					{_('Cancel')}
				</Button>,
			]}>
			<div className={styles.firmwareModal} id='updateFirmwareModal'>
				<p>{_('SelectFirmware')}</p>
				<Select
					className={styles.select}
					options={firmwares}
					onChange={handleOnSetTSFirmware}
					key='tsFirmware'
					value={tSFirmware?.toString() ?? '0'}
					getPopupContainer={() => document.getElementById('updateFirmwareModal')}
				/>
			</div>
			<div className={styles.firmwareCard}>
				<Card>
					<Descriptions>
						<Descriptions.Item>{warningMessage}</Descriptions.Item>
					</Descriptions>
				</Card>
			</div>
			{!isVeridtReader && (
				<div className={styles.firmwareModal}>
					<Checkbox checked={applyToAllReaders} onChange={handleOnApplyToAllReaders} id={'applyFirmware'}>
						<span>{_('ApplyFirmwareToAllReaders')}</span>
					</Checkbox>
				</div>
			)}
		</Modal>
	);
};

export { UpdateFirmwareModal };
